import { Discount } from 'frontend/types/premium';
import { Optional } from 'frontend/types';

export function prettyPercentage(discount: Discount): number {
  return Math.round(discount.percentage < 1 ? discount.percentage * 100 : discount.percentage);
}

/*
The following two functions `isLegacySeasonal` and `isLegacyDirectTraffic` are called Legacy
because these constructs exist temporarily to ease the refactor work previously applied to these
types.
The final objective is to not need to check anything about a `Discount`, we want to be able
to just access data from within these bundles directly (in the frontend).
Before we can do that we'll need to update the Django templates and Vue files that reference
these.
As an example, one such usage is in the GiftBanner component where we check if the Discount
`isLegacySeasonal` to print a specific banner message and a default one otherwise.
Now if all Discounts carried a `giftBannerMessage` property, the default above for example,
we would be able to replace the usage of `isLegacySeasonal` with `discount.giftBannerMessage`
and this function would be obsolete (for that use case).
Note that `isLegacySeasonal`, `isLegacyDirectTraffic` and `isDiscount` are
[Type Predicates](https://www.typescriptlang.org/docs/handbook/2/narrowing.html#using-type-predicates)
(due to the return type of discount is Discount) so if an Optional<Discount> is passed in and
isn't undefined it will be narrowed to the Discount type for the remainder of the block:
```
const someDiscount: Optional<Discount>;
if (isDiscount(someDiscount)) {
  // someDiscount is of type Discount here
  someDiscount.name // guaranteed to work
}
```
*/
export function isLegacySeasonal(discount: Optional<Discount>): discount is Discount {
  return (
    isDiscount(discount) &&
    [
      'Black Friday',
      'Cyber Monday',
      "Valentine's Day",
      "Mother's Day",
      'Holiday Season',
      "Father's Day",
      '30% Summer Sale',
    ].includes(discount.name)
  );
}

export function isLegacyDirectTraffic(discount: Optional<Discount>): discount is Discount {
  return isDiscount(discount) && ['USA'].includes(discount.name);
}

export function isDiscount(discount: Optional<Discount>): discount is Discount {
  return discount !== undefined && discount !== null;
}

export function isStudentDiscount(discount: Optional<Discount>): discount is Discount {
  return isDiscount(discount) && discount.name === 'Student';
}
